"use client";

import React, {
	useRef,
	useState,
	useEffect,
	SyntheticEvent,
	useCallback,
} from "react";

import dynamic from "next/dynamic";

import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import LinearProgress from "@mui/material/LinearProgress";
import DialogContentText from "@mui/material/DialogContentText";

import { cn } from "@onair/utils/cn";
import { useStoreLibrary } from "@onair/store/library";

import LinkTab from "./link-tab";
import DocumentTab from "./document-tab";
import LibraryTabPanel from "./library-tab-panel";
// import OnlineCourseTabPanel from "../../edit/_components/online-course-tab-panel";
import { LIBRARY_TABS, LibraryTabEnum } from "./type";
// import { useGetRootLibraryQuery } from "#src/operations";
import {
	useAdminGetRootLibraryQuery,
	useGetRootLibraryQuery,
} from "@onair/operations";
import { dialogClasses } from "@mui/material";
import { GetResourceResponseDto } from "@onair/repositories";
import { DocumentItemListProps } from "./document-item-list";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
	[`& .${dialogClasses.paper}`]: {
		maxHeight: theme.spacing(84.5),
		height: "100%",
	},
}));

const a11yProps = (index: LibraryTabEnum) => {
	return {
		id: `library-tab-${index}`,
		"aria-controls": `library-tabpanel-${index}`,
	};
};

const LibraryManagementDialog = () => {
	const {
		rootLibraryId,
		setRootLibraryId,
		isOpen: open,
		setIsOpen,
		callback,
		handleSelectResourceId: setSelectResourceId,
		isMultiple,
		resources,
		setResources,
		handleSelectIsMultiple: setMultipleResources,
	} = useStoreLibrary((state) => ({
		rootLibraryId: state.rootLibraryId,
		setRootLibraryId: state.setRootLibraryId,
		isOpen: state.isOpen,
		setResources: state.setResources,
		setIsOpen: state.setIsOpen,
		handleSelectResourceId: state.handleSelectResourceId,
		isMultiple: state.isMultiple,
		resources: state.resources,
		handleSelectIsMultiple: state.handleSelectIsMultiple,
		callback: state.callback,
	}));

	const {
		confirmSelectResourceItems,
		onOpenResource,
		setSelectResoueceItems,
		selectedResourceItems,
	} = useStoreLibrary((state) => ({
		confirmSelectResourceItems: state.confirmSelectResourceItems,
		onOpenResource: state.onOpenResource,
		setSelectResoueceItems: state.setSelectedResourceItems,
		selectedResourceItems: state.selectedResourceItems,
	}));

	const { data: rootLibrary } = useGetRootLibraryQuery({ enabled: true });

	// set root library id
	useEffect(() => {
		if (rootLibrary) {
			setRootLibraryId(rootLibrary?.id);
		}
	}, [rootLibrary]);
	const descriptionElementRef = useRef<HTMLElement>(null);
	const [tab, setTab] = useState<LibraryTabEnum>(LibraryTabEnum.DOCUMENT);
	const [currentResourceId, setCurrentResourceId] = useState<string | null>(
		null,
	);

	const previousCurrentResourceId = useRef<string | null>(null);

	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [currentFileUpload, setCurrentFileUpload] = useState<string | null>(
		null,
	);
	const [totalUploadFile, setTotalUploadFile] = useState<number>(0);
	const [totalCompleteUploadFile, setTotalCompleteUploadFile] =
		useState<number>(0);

	useEffect(() => {
		if (open) {
			const { current: descriptionElement } = descriptionElementRef;
			if (descriptionElement !== null) {
				descriptionElement.focus();
			}
		}
	}, [open]);

	const handleChange = (_: SyntheticEvent, newValue: LibraryTabEnum) => {
		setTab(newValue);
	};

	const onCloseDialog = () => {
		setResources([]);
		setIsOpen({
			isOpen: false,
		});
	};

	const handleSelectResource = useCallback((resourceId: string) => {
		setResources([resourceId]);
	}, []);

	const handleSelectMultipleResources = useCallback((resourceIds: string[]) => {
		setResources(resourceIds);
	}, []);

	const handleSelect = useCallback(() => {
		if (callback) {
			callback(resources);
			setResources([]);
		}
		onCloseDialog();
	}, [resources, previousCurrentResourceId]);

	/**
	 * new one
	 */

	const handleSelectResourceItem: DocumentItemListProps["onSelectResource"] = (
		resource,
	) => {
		console.log({ selectedResourceItems, resource });
		let updateItems = [...selectedResourceItems];
		if (isMultiple) {
			const indexItem = updateItems.findIndex(
				(item) => item.id === resource.id,
			);

			if (indexItem !== -1) {
				updateItems.splice(indexItem, 1);
			} else {
				updateItems = [...updateItems, resource];
			}
		} else {
			if (!updateItems.length || updateItems[0].id !== resource.id) {
				updateItems = [resource];
			}
		}

		setSelectResoueceItems?.(updateItems);
	};
	const handleConfirmSelectionResourceItems = () => {
		if (!confirmSelectResourceItems) return;

		confirmSelectResourceItems(selectedResourceItems);
		setSelectResoueceItems?.([]);
	};

	return (
		<BootstrapDialog
			open={open}
			onClose={onCloseDialog}
			maxWidth="lg"
			fullWidth
			aria-labelledby="scroll-dialog-title"
			aria-describedby="scroll-dialog-description"
		>
			<DialogTitle className="border-b border-dashed" id="scroll-dialog-title">
				<Stack className="flex flex-row justify-between items-center">
					<Typography variant="h6">Thư viện của bạn</Typography>
					<IconButton
						aria-label="close"
						onClick={onCloseDialog}
						className="p-0 min-w-0 min-h-0"
					>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent className="pt-6" dividers>
				<Stack className="mb-4">
					<Stack direction="row" justifyContent="space-between">
						<Tabs
							value={tab}
							onChange={handleChange}
							textColor="secondary"
							indicatorColor="secondary"
							aria-label="library managment tabs"
							className="border-white"
						>
							{LIBRARY_TABS.map((tab) => (
								<Tab
									key={tab.value}
									value={tab.value}
									label={tab.label}
									{...a11yProps(tab.value)}
								/>
							))}
						</Tabs>
					</Stack>
				</Stack>

				<DialogContent
					id="scroll-dialog-description"
					ref={descriptionElementRef}
					tabIndex={-1}
					className="!px-0"
				>
					<Box>
						{tab === LibraryTabEnum.DOCUMENT ? (
							<DocumentTab
								rootLibraryId={rootLibraryId}
								selectResourceId={resources[0]}
								setSelectResourceId={handleSelectResource}
								currentResourceId={currentResourceId}
								setCurrentResourceId={setCurrentResourceId}
								setProgress={setUploadProgress}
								setCurrentFileUpload={setCurrentFileUpload}
								setTotalUploadFile={setTotalUploadFile}
								setTotalCompleteUploadFile={setTotalCompleteUploadFile}
								isMultiple={isMultiple}
								multipleResources={resources}
								setMultipleResources={handleSelectMultipleResources}
								onSelectResourceItem={handleSelectResourceItem}
							/>
						) : (
							<LinkTab rootLibraryId={rootLibraryId} />
						)}
					</Box>
				</DialogContent>
			</DialogContent>

			<DialogActions>
				<Box width="100%" justifyContent="space-between" className="flex">
					<Stack
						width="60%"
						direction="row"
						spacing={1}
						alignItems="center"
						className={cn(
							totalCompleteUploadFile < totalUploadFile ? "" : "opacity-0",
						)}
					>
						<Typography variant="body2" minWidth={80} className="text-sm">
							{`Tải lên: ${totalCompleteUploadFile}/${totalUploadFile}`}
						</Typography>
						<Box className="w-1/2">
							<LinearProgress variant="determinate" value={uploadProgress} />
						</Box>

						<Box>
							<Typography variant="body2" minWidth={80} className="text-sm">
								{`${uploadProgress} %`}
							</Typography>
						</Box>
						{currentFileUpload && (
							<Box sx={{ flex: 1 }}>
								<Typography
									variant="subtitle1"
									className="text-sm"
									sx={{
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{currentFileUpload}
								</Typography>
							</Box>
						)}
					</Stack>
					<Stack direction="row" spacing={1}>
						<Button onClick={onCloseDialog} variant="outlined">
							Đóng
						</Button>
						<Button
							onClick={() => {
								handleSelect();
								handleConfirmSelectionResourceItems();
							}}
							color="primary"
							variant="contained"
						>
							Chọn
						</Button>
					</Stack>
				</Box>
			</DialogActions>
		</BootstrapDialog>
	);
};

export default LibraryManagementDialog;
